import { useState } from 'react'
import { Star } from 'lucide-react'

/**
 * Star rating component.
 *
 * Props:
 * - value: current rating (0-5)
 * - onChange: callback when a star is clicked (omit for read-only)
 * - orientation: 'horizontal' | 'vertical'
 * - size: icon size in pixels
 * - activeClass: Tailwind class for filled stars
 * - inactiveClass: Tailwind class for empty stars
 *
 * In vertical mode, star 1 is rendered at the bottom so the fill grows
 * upward (matches the design mock-ups).
 */
export default function StarRating({
  value,
  onChange,
  orientation = 'horizontal',
  size = 18,
  activeClass = 'text-brand-gold',
  inactiveClass = 'text-gray-400',
}) {
  const [hover, setHover] = useState(0)
  const isInteractive = Boolean(onChange)
  const isHorizontal = orientation === 'horizontal'
  const activeValue = hover || value

  // In vertical mode, render 5 → 1 top-to-bottom so the fill grows from
  // the bottom (value 1) to the top (value 5).
  const starValues = isHorizontal ? [1, 2, 3, 4, 5] : [5, 4, 3, 2, 1]

  return (
    <div
      className={`flex ${isHorizontal ? 'flex-row' : 'flex-col'} items-center gap-0.5`}
      onMouseLeave={() => isInteractive && setHover(0)}
      role={isInteractive ? 'group' : undefined}
      aria-label={isInteractive ? `Ocena ${value} na 5` : `Ocena ${value} na 5`}
    >
      {starValues.map((n) => (
        <button
          key={n}
          type="button"
          disabled={!isInteractive}
          onClick={() => isInteractive && onChange(n)}
          onMouseEnter={() => isInteractive && setHover(n)}
          className={`p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded ${
            isInteractive ? '' : 'cursor-default'
          }`}
          aria-label={isInteractive ? `Oceń ${n} na 5` : undefined}
        >
          <Star
            size={size}
            fill={n <= activeValue ? 'currentColor' : 'none'}
            className={n <= activeValue ? activeClass : inactiveClass}
          />
        </button>
      ))}
    </div>
  )
}
